// MAJLIS-71 (D-52, UI-C19, UI-C10, EM-12, D-42, TS-3): the owner of the one microphone for as long as the room is open.
// Before this, the room opened the microphone ONCE and never again, and nothing listened for its track ENDING (an OS
// capture failure, a Bluetooth switch, Chrome ending a silent capture): the seat went on reading unmuted while nothing
// arrived, and the ring showed `idle`. The consultant's ruling (the hybrid, option (c) of the proposal, 2026-09-24):
//
//   live --ended, the person had muted--> ended       (the ring reads `muted`; no getUserMedia while muted)
//        ended --the unmute tap--> reopening(tap)     (inside the tap, synchronously: it counts as a gesture)
//   live --ended, unmuted--> the seat is HELD MUTED   (the EXISTING mute message: others see "muted"; the local button
//                                                      shows `stopped`, never `muted`)
//        within FLAP_WINDOW_MS of the last re-open --> stopped           (no silent loop)
//        page hidden --> waiting --visible--> reopening(silent)
//        page visible --> reopening(silent), bounded by REOPEN_TIMEOUT_MS on the injected clock
//   reopening(silent) --ok--> live, and the hold is lifted (the unmute message: he never chose the mute)
//                     --ANY refusal, or the bound--> stopped   (Q1: a refusal with no tap is `stopped`, never `denied`)
//   stopped / waiting / reopening(silent) --the tap--> reopening(tap), and the caller unmutes in the same tap
//   reopening(tap) --ok--> live
//                  --NotAllowedError / SecurityError--> denied   (only an explicit permission refusal is `denied`)
//                  --NotFoundError / OverconstrainedError / no mediaDevices--> unavailable
//                  --any other failure--> stopped, the seat held muted again
//   any --close--> closed   (a late result is stopped, never handed on)
//
// Q3: `ended` ONLY. A track that is muted by the OS but still live resumes on its own `unmute` event: nothing here
// listens for `mute`, and nothing is re-opened for it.
// TS-3: every open goes through the ONE opener (mic.ts, its one getUserMedia call), one call per attempt, and the ended
// track is stopped BEFORE the call (Safari: a second capture started beside the old one can end with "capture failure";
// livekit-client's own restart() carries the same rule). The ring's AudioContext keeper (MAJLIS-98) is part of the source
// mic.ts builds, so it is released with the old source and made again on the new track.
// EM-25 / D-42: the new source goes to the gate before the publisher (the caller's `source`, in that order), so the new
// track is never enabled while a replay or a voice holds the gate; the publisher unpublishes the ended track and
// publishes the new one through Publisher.setTrack (already tested). No server change: the mute and unmute are the
// existing P-3 messages.
import type { Clock, TimerId } from "../clock";
import { FLAP_WINDOW_MS, REOPEN_TIMEOUT_MS } from "../constants";
import type { MicOpened, MicOpener, MicSource } from "./mic";
import type { MicPermission } from "./micRing";

export type KeeperState =
  | "opening" // the room's first open, as before (no bound: a permission prompt may take its time)
  | "live"
  | "denied"
  | "unavailable"
  | "ended" // ended while the person had muted: the unmute tap re-opens
  | "waiting" // ended while unmuted with the page hidden: the silent re-open waits for `visible`
  | "reopening"
  | "stopped" // the tap state (UI-C19 `stopped`)
  | "closed";

/** What the keeper needs of the page: whether it is visible, and to be told when it becomes so. */
export interface KeeperPage {
  visible(): boolean;
  onVisible(fn: () => void): () => void;
}

export interface KeeperEvents {
  /** The current source (null: none) and the permission. The caller attaches it to the gate FIRST, then hands its
   * track to the publisher. */
  source(source: MicSource | null, permission: MicPermission): void;
  /** true: the microphone stopped while he was unmuted - send the existing mute message. false: a SILENT re-open
   * brought it back - send the unmute (he never chose that mute). The tap's own unmute is the caller's. */
  hold(muted: boolean): void;
  /** Anything the room shows changed (state, hold). */
  changed(): void;
}

export interface KeeperDeps {
  open: MicOpener;
  clock: Clock;
  page: KeeperPage;
  events: KeeperEvents;
  /** Resume the ring's AudioContext inside the tap (mic.ts primeAudio: iOS needs the gesture); called before the open. */
  prime?: () => void;
}

type Attempt = "open" | "silent" | "tap";

/** A tap's refusal that is an explicit permission denial (Q1); every other failure of a tap is `stopped`. */
const DENIALS = new Set(["NotAllowedError", "SecurityError"]);

/** The page as the browser has it. */
export function documentPage(doc: Document = document): KeeperPage {
  return {
    visible: () => doc.visibilityState === "visible",
    onVisible(fn) {
      const on = () => {
        if (doc.visibilityState === "visible") fn();
      };
      doc.addEventListener("visibilitychange", on);
      return () => doc.removeEventListener("visibilitychange", on);
    },
  };
}

export class MicKeeper {
  state: KeeperState = "opening";

  private source: MicSource | null = null;
  private unwatch: (() => void) | null = null;
  /** The person's own mute (the seat's record), as last told; not changed by our own hold. */
  private muted = false;
  /** The seat reads muted because the microphone stopped, not because he chose it. */
  private held = false;
  /** The current attempt's number: any other attempt's result is late, and its tracks are stopped. */
  private seq = 0;
  private attempting: Attempt | null = null;
  private timer: TimerId | undefined;
  private lastReopenAt: number | null = null;
  private readonly unvisible: () => void;

  constructor(private readonly deps: KeeperDeps) {
    this.unvisible = deps.page.onVisible(() => {
      if (this.state === "waiting") this.attempt("silent");
    });
  }

  /** The room's first open (once per room). */
  start(): void {
    if (this.state === "opening" && this.attempting === null) this.attempt("open");
  }

  /** The seat's mute as the person set it (our own hold is ignored here: it is not his). */
  setMuted(muted: boolean): void {
    if (this.held || this.state === "closed") return;
    this.muted = muted;
    // unmuted by something other than this phone's tap while the track lay ended: as if it ended now, unmuted
    if (!muted && this.state === "ended") this.afterEnded();
  }

  /** The ring shows `stopped` (UI-C19): the track ended and no re-open has succeeded yet, and he did not mute. */
  stopped(): boolean {
    return this.state === "waiting" || this.state === "reopening" || this.state === "stopped";
  }

  /** The seat reads muted because of the stop (the ring's `muted` is his own mute only). */
  holding(): boolean {
    return this.held;
  }

  /** The mic button's tap must re-open the microphone (and the caller then unmutes in the same tap). */
  needsTap(): boolean {
    return (
      this.state === "ended" ||
      this.state === "waiting" ||
      this.state === "stopped" ||
      (this.state === "reopening" && this.attempting === "silent")
    );
  }

  /** The tap: getUserMedia is CALLED before this returns (inside the gesture). A silent attempt still out is
   * superseded; a tap's own attempt still out is not stacked. */
  tap(): void {
    if (!this.needsTap()) return;
    this.held = false; // the caller unmutes in this same tap
    this.muted = false;
    this.deps.prime?.();
    this.attempt("tap");
  }

  /** Leave, or the room screen going away: stop the source; any result still out is stopped when it lands. */
  close(): void {
    if (this.state === "closed") return;
    this.state = "closed";
    this.seq += 1;
    this.attempting = null;
    this.clearTimer();
    this.unvisible();
    this.unwatch?.();
    this.unwatch = null;
    this.source?.stop();
    this.source = null;
  }

  // ---------------------------------------------------------------- internals

  private attempt(kind: Attempt): void {
    const id = ++this.seq;
    this.clearTimer();
    this.attempting = kind;
    if (kind !== "open") this.set("reopening");
    // only a SILENT attempt is bounded: a tap's may be showing a permission prompt, which takes its own time
    if (kind === "silent") this.timer = this.deps.clock.setTimeout(() => this.timedOut(id), REOPEN_TIMEOUT_MS);
    this.deps.open().then(
      (r) => this.settled(id, kind, r),
      (e: unknown) => this.settled(id, kind, { permission: "unavailable", source: null, error: errorName(e) }),
    );
  }

  private settled(id: number, kind: Attempt, r: MicOpened): void {
    if (id !== this.seq || this.state === "closed") {
      r.source?.stop(); // late: timed out, superseded or closed - never handed on
      return;
    }
    this.clearTimer();
    this.attempting = null;
    const source = r.permission === "granted" ? r.source : null;
    if (source && kind !== "open" && source.track?.readyState === "ended") {
      source.stop(); // it ended before it could be used: a failed attempt
      return this.toStopped();
    }
    if (source) {
      if (kind !== "open") this.lastReopenAt = this.deps.clock.now();
      const wasHeld = this.held;
      this.held = false;
      this.state = "live";
      this.adopt(source);
      if (wasHeld && this.state === "live") this.deps.events.hold(false);
      this.deps.events.changed();
      return;
    }
    r.source?.stop();
    if (kind === "silent") return this.toStopped(); // Q1: never `denied` without a tap
    if (kind === "tap" && r.permission === "denied" && r.error !== undefined && !DENIALS.has(r.error)) return this.toStopped();
    // the room's first open, and a tap's explicit refusal or missing device: as the room has always shown them
    this.state = r.permission === "denied" ? "denied" : "unavailable";
    this.deps.events.source(null, this.state);
    this.deps.events.changed();
  }

  private timedOut(id: number): void {
    this.timer = undefined;
    if (id !== this.seq) return;
    this.seq += 1; // its result, when it comes, is late
    this.attempting = null;
    this.toStopped();
  }

  private adopt(source: MicSource): void {
    this.source = source;
    this.deps.events.source(source, "granted");
    const track = source.track;
    if (!track || typeof track.addEventListener !== "function") return;
    const onEnded = () => this.ended(source);
    track.addEventListener("ended", onEnded);
    this.unwatch = () => track.removeEventListener("ended", onEnded);
    if (track.readyState === "ended") this.ended(source); // it ended between the open and here
  }

  private ended(source: MicSource): void {
    if (source !== this.source || this.state === "closed") return;
    // stop and forget the old source FIRST (Safari: before any new getUserMedia), then the gate and the publisher
    this.unwatch?.();
    this.unwatch = null;
    this.source = null;
    source.stop();
    this.deps.events.source(null, "granted");
    if (this.muted) {
      this.set("ended"); // he had muted: the unmute tap re-opens, in its gesture
      return;
    }
    this.afterEnded();
  }

  /** The track lies ended and the seat is not muted by him: hold it muted, then re-open silently if allowed. */
  private afterEnded(): void {
    this.hold();
    const last = this.lastReopenAt;
    if (last !== null && this.deps.clock.now() - last < FLAP_WINDOW_MS) return this.set("stopped");
    if (this.deps.page.visible()) this.attempt("silent");
    else this.set("waiting");
  }

  private toStopped(): void {
    this.hold();
    this.set("stopped");
  }

  private hold(): void {
    if (this.held) return;
    this.held = true;
    this.deps.events.hold(true);
  }

  private set(state: KeeperState): void {
    this.state = state;
    this.deps.events.changed();
  }

  private clearTimer(): void {
    if (this.timer !== undefined) this.deps.clock.clearTimeout(this.timer);
    this.timer = undefined;
  }
}

function errorName(e: unknown): string {
  const name = (e as { name?: unknown } | null)?.name;
  return typeof name === "string" ? name : "Error";
}
